import React, { useEffect, useState } from 'react';
import axios from 'axios';
import LoadingLogo from './LoadingLogo';
import Seo from './Seo';

const ProductList = ({ withSeo = true }) => {
  const [products, setProducts] = useState([]);
  const [lastResponse, setLastResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const isLoggedIn = !!(localStorage.getItem('token') && localStorage.getItem('user'));
  const [showFullDescription, setShowFullDescription] = useState({});

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `token ${token}` } : {};
      const res = await axios.get('https://apifusion.synergyindustri.com/api/blog-posts-getall/', { headers });
      setLastResponse(res.data);
      const items = Array.isArray(res.data)
        ? res.data
        : (res.data?.results || res.data?.data || res.data?.items || []);
      setProducts(Array.isArray(items) ? items : []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePicture = (postId) => {
    const onFile = (event) => {
      const imageFile = event.target.files && event.target.files[0];
      if (!imageFile) return;

      // Try multipart/form-data first (send actual File). If server rejects (expects base64), fall back to base64 JSON.
      const tryMultipartUpdate = async () => {
        const formData = new FormData();
        const title = prompt('Enter the new title:');
        const description = prompt('Enter the new description:');
        if (!title || !description) return;
        formData.append('image', imageFile);
        formData.append('title', title);
        formData.append('description', description);

        const token = localStorage.getItem('token');
        const headers = { Authorization: `token ${token}` };

        // Debug: log formData entries
        try { for (const e of formData.entries()) console.log('FormData (update):', e[0], e[1]); } catch (e) { console.warn('Could not enumerate FormData entries', e); }

        try {
          await axios.put(`https://apifusion.synergyindustri.com/api/blog/${postId}/`, formData, { headers });
          fetchProducts();
          return;
        } catch (error) {
          console.error('Error editing product (multipart):', error);
          console.error('Error response data (update):', JSON.stringify(error.response?.data));
          const imageFieldErrors = error.response?.data?.image;
          const shouldFallback = Array.isArray(imageFieldErrors) && imageFieldErrors.some(msg => String(msg).toLowerCase().includes('not a valid string'));
          if (!shouldFallback) {
            try { alert('Error editing product: ' + JSON.stringify(error.response?.data || error.message)); } catch (e) {}
            return;
          }
        }

        // Fallback: read as base64 then send JSON
        const reader = new FileReader();
        reader.onload = async (ev) => {
          const base64Image = ev.target.result;
          try {
            await axios.put(
              `https://apifusion.synergyindustri.com/api/blog/${postId}/`,
              { image: base64Image, title, description },
              { headers: { Authorization: `token ${token}`, 'Content-Type': 'application/json' } }
            );
            fetchProducts();
          } catch (err2) {
            console.error('Fallback base64 update failed:', err2, err2.response?.data);
            try { alert('Error editing product (fallback): ' + JSON.stringify(err2.response?.data || err2.message)); } catch (e) {}
          }
        };
        reader.readAsDataURL(imageFile);
      };

      tryMultipartUpdate();
    };

    const inputElement = document.createElement('input');
    inputElement.type = 'file';
    inputElement.accept = 'image/*';
    inputElement.addEventListener('change', onFile);
    inputElement.click();
  };

  const handleAddImage = () => {
    const onFile = (event) => {
      const imageFile = event.target.files && event.target.files[0];
      if (!imageFile) return;

      const title = prompt('Enter the title:');
      const description = prompt('Enter the description:');
      if (!title || !description) return;

      // Try multipart/form-data first (File). If server rejects with "Not a valid string.", fall back to base64 JSON.
      const tryMultipartCreate = async () => {
        const formData = new FormData();
        formData.append('image', imageFile);
        formData.append('title', title);
        formData.append('description', description);

        const token = localStorage.getItem('token');
        const headers = { Authorization: `token ${token}` };

        // Debug: enumerate entries
        try { for (const e of formData.entries()) console.log('FormData (create):', e[0], e[1]); } catch (e) { console.warn('Could not enumerate FormData entries', e); }

        try {
          await axios.post('https://apifusion.synergyindustri.com/api/blog/create/', formData, { headers });
          fetchProducts();
          return;
        } catch (error) {
          console.error('Error adding product (multipart):', error);
          console.error('Error response data (create):', JSON.stringify(error.response?.data));
          const imageFieldErrors = error.response?.data?.image;
          const shouldFallback = Array.isArray(imageFieldErrors) && imageFieldErrors.some(msg => String(msg).toLowerCase().includes('not a valid string'));
          if (!shouldFallback) {
            try { alert('Error adding product: ' + JSON.stringify(error.response?.data || error.message)); } catch (e) {}
            return;
          }
        }

        // Fallback to base64 JSON
        const reader = new FileReader();
        reader.onload = async (ev) => {
          const base64Image = ev.target.result;
          try {
            await axios.post('https://apifusion.synergyindustri.com/api/blog/create/', { image: base64Image, title, description }, { headers: { Authorization: `token ${token}`, 'Content-Type': 'application/json' } });
            fetchProducts();
          } catch (err2) {
            console.error('Fallback base64 create failed:', err2, err2.response?.data);
            try { alert('Error adding product (fallback): ' + JSON.stringify(err2.response?.data || err2.message)); } catch (e) {}
          }
        };
        reader.readAsDataURL(imageFile);
      };

      tryMultipartCreate();
    };

    const inputElement = document.createElement('input');
    inputElement.type = 'file';
    inputElement.accept = 'image/*';
    inputElement.addEventListener('change', onFile);
    inputElement.click();
  };

  const handleDelete = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`https://apifusion.synergyindustri.com/api/blog/${postId}/delete/`, {
        headers: { Authorization: `token ${token}` },
      });
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const toggleDescription = (id) => {
    setShowFullDescription(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <>
      {withSeo ? (
        <Seo
          title={`Some of Our Products - Fusion Tech`}
          description={`Browse Fusion Tech's products and catalog.`}
        />
      ) : null}

      <div className="container mx-auto px-6 py-10">
        <h1 className="text-4xl font-bold text-center mb-8">Some of Our Products</h1>

        {isLoggedIn && (
          <div className="mb-6 text-right">
            <button onClick={handleAddImage} className="inline-block bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded">
              Add Product
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12"><LoadingLogo /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.length ? products.map(p => (
              <div key={p.id} className="bg-white rounded-lg shadow hover:shadow-2xl overflow-hidden">
                <img src={p.image} alt={p.title || p.name} className="w-full h-44 object-cover" />
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-1">{p.title || p.name}</h3>
                  <p className="text-gray-600 text-sm mb-3" style={{ maxHeight: showFullDescription[p.id] ? 'none' : '3.6rem', overflow: 'hidden' }}>{p.description || p.summary || p.body}</p>
                  <div className="flex items-center justify-between">
                    <button onClick={() => toggleDescription(p.id)} className="text-sm text-fusion-blue">{showFullDescription[p.id] ? 'Less' : 'More'}</button>
                    <div className="flex gap-2">
                      {isLoggedIn && (
                        <>
                          <button onClick={() => handleChangePicture(p.id)} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm">Edit</button>
                          <button onClick={() => handleDelete(p.id)} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm">Delete</button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )) : (
              <div className="col-span-full text-center text-sm text-gray-600">
                No products were returned from the API.
                {lastResponse ? (
                  <div className="mt-3 text-left overflow-auto max-h-48 bg-white p-2 border rounded text-xs">
                    <pre>{JSON.stringify(lastResponse, null, 2)}</pre>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ProductList;









































































































































































































































































// import React, { useEffect, useState } from 'react';
// import axios from 'axios';

// const BlogPostList = () => {
//     const [blogPosts, setBlogPosts] = useState([]);
    
//     useEffect(() => {
//       fetchBlogPosts();
//     }, []);

//     const fetchBlogPosts = () => {
//       const token = localStorage.getItem('token');
//       const headers = {
//         'Content-Type': 'multipart/form-data',
//       };
//       axios
//         .get('https://apifusion.synergyindustri.com/api/blog/')
//         .then((response) => {
//           setBlogPosts(response.data);
//         })
//         .catch((error) => {
//           console.error('Error fetching blog posts:', error);
//         });
//     };


  
//   return (
//     <div className="shadow-lg rounded-lg bg-gray-200 mx-auto w-11/12 mt-4">
//     <h1 className='flex flex-row justify-center items-center font-semibold p-3 py-10 text-4xl mt-20 text-center'>Some of Our Products</h1>
//     <>
//     <div className="flex flex-wrap justify-center ">
//       {blogPosts.map((post) => (
//         <div key={post.id} className="w-full lg:w-1/4 p-4">
//           <div className="bg-white rounded-lg shadow-lg">
//             <img src={post.image} alt={post.name} className="h-48 w-full rounded-t-lg " />
//             <div className="p-4">

//             <div className='overflow-y-auto bg w-full'>
//               <p className="text-lg font-semibold">{post.title}</p>
//             </div>
//               <div className='overflow-y-auto bg w-full p-1'>
//               <h3 className="mt-2 text-gray-600">{post.description}</h3>
//             </div>
//             </div>
//           </div>
//         </div>
//       ))}
  
//     </div>
//     </>
//     )}
//     </div>
//   );
// };

// export default BlogPostList;


























